import * as React from 'react';
import { useCallback } from 'react';
import { Field, FieldMetaState, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { InfoMessageTypeEnum, CheckoutOrderFragment, CheckinOrderFragment } from '@websky/graphql';
import { useTheme } from '../../theme';
import { useIsMobile } from '../../utils';
import { useRetrieveBookingValidation } from '../hooks';
import { useRetrieveBooking } from './RetrieveBooking.hook';
import { BaseButton } from '../../BaseComponents';
import Input from '../../BaseComponents/Input';
import Tooltip from '../../BaseComponents/Tooltip';
import ClarifyDocumentModal from './ClarifyDocumentModal/ClarifyDocumentModal';
import Notification from '../../Notification';
import BookingPreview from '../../BookingPreview';
import { InfoQuestion } from '../../Icons';

export enum Mode {
	Checkout,
	Checkin
}

interface SubmitParamsProps {
	id: string;
	secret?: string;
	extraSecret?: string;
	secret2fa?: string;
	saveInProfile?: boolean;
}

export interface Props {
	mode: Mode;
	className?: string;
	initialId?: string;
	initialSecret?: string;
	title?: string;
	orderPreview?: CheckoutOrderFragment;
	submitWithInitialValues?: boolean;
	idTitle?: string;
	secretTitle?: string;
	onSubmit: (params: SubmitParamsProps) => Promise<CheckinOrderFragment | boolean>;
	withBookingPreview?: boolean;
	onHideOrderPreview?: () => void;
	isOrderOwner?: boolean;
}

const RetrieveBooking: React.FC<Props> = props => {
	const {
		mode,
		className,
		title,
		initialId,
		initialSecret,
		idTitle,
		secretTitle,
		withBookingPreview,
		onHideOrderPreview
	} = props;

	const { t, i18n } = useTranslation('RetrieveBooking');
	const { RetrieveBooking: css } = useTheme('RetrieveBooking');

	const isMobile = useIsMobile();
	const showOrderPreview = !!props.orderPreview;

	const model = useRetrieveBooking(props);

	const {
		lastNameValidation,
		ticketNumberValidation,
		validateByOrderNumber,
		ticketNumberFormat
	} = useRetrieveBookingValidation();

	const inputVariant = isMobile ? 'underline' : 'labelIn';

	const renderTooltip = useCallback(
		(meta: FieldMetaState<string>) => {
			if (meta.data.warning && mode === Mode.Checkin) {
				return (
					<Tooltip tooltipClassName={css.tooltip} title={t(meta.data.warning)} open={true}>
						<div className={css.tooltip__icon} data-mode="checkin">
							{InfoQuestion}
						</div>
					</Tooltip>
				);
			}

			return (
				<Tooltip title={t('You can find security code in your booking confirmation email')}>
					<div className={css.tooltip__icon} data-mode={mode === Mode.Checkin ? 'checkin' : 'checkout'}>
						{InfoQuestion}
					</div>
				</Tooltip>
			);
		},
		[mode]
	);

	return (
		<div
			className={cn(css.container, className, {
				[css.container_checkin]: mode === Mode.Checkin
			})}
		>
			<div className={css.title}>{title ?? t('Retrieve booking')}</div>

			<ClarifyDocumentModal
				searchParams={model.searchParams}
				onSubmit={model.handleClarifySubmit}
				open={model.isClarityDocumentOpen}
				onClose={model.closeClarityDocument}
			/>

			<Form onSubmit={model.handleSubmit}>
				{({ handleSubmit, valid }) => (
					<form className={css.form} onSubmit={handleSubmit}>
						<div className={css.form__row}>
							<Field
								name="secret"
								initialValue={initialSecret}
								validate={(value: string, allValues, meta): string | void => {
									if (!value) {
										return t('Required field') as string;
									}

									if (mode === Mode.Checkin) {
										return lastNameValidation(value);
									}
								}}
								render={({ input, meta }) => (
									<>
										<Input
											{...input}
											autoComplete={'one-time-code'}
											className={css.form__input}
											disabled={model.isLoading || (withBookingPreview && showOrderPreview)}
											label={secretTitle || t('Security code')}
											variant={inputVariant}
											error={
												model.isOrderNotFoundError ||
												model.isBlockedForPayment ||
												(((meta.modified && meta.touched) || meta.submitFailed) &&
													(!!meta.error || !!meta.submitError))
											}
											helperText={
												(model.isOrderNotFoundError && t('Order not found')) ||
												(model.isBlockedForPayment &&
													t(
														'The order is being paid at the moment, please try again later'
													)) ||
												(((meta.modified && meta.touched) || meta.submitFailed) &&
													(meta.error || meta.submitError))
											}
										/>
									</>
								)}
							/>
						</div>
						<div className={css.form__row}>
							<Field
								name="id"
								initialValue={initialId}
								parse={ticketNumberFormat}
								validate={(value: string): string | void => {
									if (!value) {
										return t('Required field') as string;
									}

									if (mode === Mode.Checkin) {
										return ticketNumberValidation(value);
									}

									if (mode === Mode.Checkout) {
										if (!ticketNumberValidation(value) || !validateByOrderNumber(value)) {
											return undefined;
										} else {
											return ticketNumberValidation(value) || validateByOrderNumber(value);
										}
									}
								}}
								render={({ input, meta }) => (
									<>
										<Input
											{...input}
											autoComplete={'one-time-code'}
											className={css.form__input}
											disabled={model.isLoading || (withBookingPreview && showOrderPreview)}
											label={idTitle || t('Booking ID')}
											variant={inputVariant}
											helperText={
												((meta.modified && meta.touched) || meta.submitFailed) &&
												(meta.error || meta.submitError)
											}
											error={
												((meta.modified && meta.touched) || meta.submitFailed) &&
												(!!meta.error || !!meta.submitError)
											}
										/>
										{mode === Mode.Checkin && renderTooltip(meta)}
									</>
								)}
							/>
						</div>
						{model.codeIsWrong && model.isErrorNotification && (
							<Notification
								status={InfoMessageTypeEnum.Warning}
								contextClassName={css.notification_context}
								textClassName={css.notification__text}
								infoMessage={{
									text: t(
										'Booking not found. Please, check the information entered or use your e-ticket number for identification'
									),
									type: InfoMessageTypeEnum.Warning,
									code: 'warning'
								}}
								type="context"
							/>
						)}
						{model.isOnlyAirportCheckin && (
							<Notification
								status={InfoMessageTypeEnum.Warning}
								contextClassName={css.notification_context}
								textClassName={css.notification__text}
								infoMessage={{
									text: t('Online check-in is not available. Check-in only at the airport'),
									type: InfoMessageTypeEnum.Warning,
									code: 'warning'
								}}
								type="context"
							/>
						)}
						{model.isLoading || showOrderPreview ? (
							<BookingPreview
								redirectViaHistory
								order={props.orderPreview}
								onClearOrder={onHideOrderPreview}
								isLoading={model.isLoading}
								isOrderOwner={props.isOrderOwner}
							/>
						) : (
							<div className={cn(css.form__row, css.form__row_footer)}>
								{model.isCheckinMode && i18n.exists('RetrieveBooking:checkinHint') && (
									<div className={css.checkinHint}>{t('RetrieveBooking:checkinHint')}</div>
								)}

								<BaseButton
									className={cn(css.button, {
										[css.button_disabled]: !valid
									})}
									variant="action"
									type="submit"
									disabled={!valid}
									isLoading={model.isLoading}
								>
									{t('Open')}
								</BaseButton>
							</div>
						)}
					</form>
				)}
			</Form>
		</div>
	);
};

export default RetrieveBooking;
